import api from '../lib/api';
import { Project, ProjectType, NonBillableClassification, Phase, User, PaginatedResponse } from '../types';

export interface ProjectDetail extends Project {
  userProjects: {
    user: Pick<User, 'id' | 'name' | 'email'>;
  }[];
}

export const projectsApi = {
  list: (params?: { mine?: boolean }) =>
    api.get<Project[]>('/api/v1/projects', { params }),
  listPaginated: (params: { page: number; limit?: number; name?: string; includeMembers?: boolean; sortBy?: 'name' | 'client' | 'createdAt'; sortDir?: 'asc' | 'desc' }) =>
    api.get<PaginatedResponse<Project>>('/api/v1/projects', { params }),
  /** Every project with its members, fetched in pages of 100 (the server's cap). */
  listAllWithMembers: async (signal?: AbortSignal): Promise<Project[]> => {
    const projects: Project[] = [];
    for (let page = 1; ; page++) {
      const { data } = await api.get<PaginatedResponse<Project>>('/api/v1/projects', {
        params: { page, limit: 100, includeMembers: true },
        signal,
      });
      projects.push(...data.data);
      if (page >= data.meta.totalPages) return projects;
    }
  },
  get: (id: string) =>
    api.get<ProjectDetail>(`/api/v1/projects/${id}`),
  create: (body: { name: string; client?: string; typeId: string; billable?: boolean; nonBillableClassification?: NonBillableClassification; primaryScrumId?: string; secondaryScrumId?: string; projectLeadId?: string }) =>
    api.post<Project>('/api/v1/projects', body),
  update: (id: string, body: { name?: string; client?: string; status?: 'ACTIVE' | 'CLOSED' | 'FROZEN'; typeId?: string; billable?: boolean; nonBillableClassification?: NonBillableClassification; primaryScrumId?: string | null; secondaryScrumId?: string | null; projectLeadId?: string | null }) =>
    api.patch<Project>(`/api/v1/projects/${id}`, body),
  assignUser: (projectId: string, userId: string) =>
    api.post(`/api/v1/projects/${projectId}/assign`, { userId }),
  unassignUser: (projectId: string, userId: string) =>
    api.delete(`/api/v1/projects/${projectId}/assign/${userId}`),
};

export const projectTypesApi = {
  list: () =>
    api.get<ProjectType[]>('/api/v1/project-types'),
  create: (body: { name: string }) =>
    api.post<ProjectType>('/api/v1/project-types', body),
  update: (id: string, body: { name: string }) =>
    api.patch<ProjectType>(`/api/v1/project-types/${id}`, body),
  delete: (id: string) =>
    api.delete(`/api/v1/project-types/${id}`),
  addPhase: (typeId: string, body: { name: string; order?: number }) =>
    api.post<Phase>(`/api/v1/project-types/${typeId}/phases`, body),
  updatePhase: (typeId: string, phaseId: string, body: { name?: string; order?: number }) =>
    api.patch<Phase>(`/api/v1/project-types/${typeId}/phases/${phaseId}`, body),
  deletePhase: (typeId: string, phaseId: string) =>
    api.delete(`/api/v1/project-types/${typeId}/phases/${phaseId}`),
};
